import React, { useState, useEffect, useRef } from 'react';
import {
  SearchIcon,
  EyeIcon,
  PencilIcon,
  ShieldCheckIcon,
  ShieldExclamationIcon,
  RefreshIcon,
  UserIcon,
  OfficeBuildingIcon,
  XIcon,
  UserAddIcon
} from '@heroicons/react/outline';
import api from '../../services/api';
import { formatDate, formatDateTime } from '../../utils/formatDate';

// Blank form for creating a customer login. business_type mirrors the options
// offered at self-registration.
const EMPTY_CUSTOMER = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  user_type: 'individual',
  company_name: '',
  business_type: 'other',
  gst_number: ''
};

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    userType: '',
    verificationStatus: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0
  });

  // Modal states
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [resetSending, setResetSending] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CUSTOMER);
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [createResult, setCreateResult] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: ''
  });

  const reqIdRef = useRef(0);

  // Any filter change resets to page 1 so the (server-paginated) results stay coherent.
  const updateFilters = (patch) => {
    setFilters(prev => ({ ...prev, ...patch }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };
  const resetFilters = (next) => {
    setFilters(next);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Debounced fetch; the reqId guard drops out-of-order responses.
  useEffect(() => {
    const t = setTimeout(fetchUsers, 300);
    return () => clearTimeout(t);
  }, [filters.search, filters.userType, filters.verificationStatus, pagination.page]);

  const fetchUsers = async () => {
    const reqId = ++reqIdRef.current;
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        limit: pagination.limit,
        skip: (pagination.page - 1) * pagination.limit,
        ...(filters.search && { search: filters.search }),
        ...(filters.userType && { user_type: filters.userType }),
        ...(filters.verificationStatus && { verification_status: filters.verificationStatus })
      });

      const response = await api.get(`/admin/users?${params}`);

      if (reqId !== reqIdRef.current) return; // superseded by a newer request
      setUsers(response.data.data.users);
      setPagination(prev => ({ ...prev, total: response.data.data.total_count }));
    } catch (error) {
      if (reqId !== reqIdRef.current) return;
      console.error('Error fetching users:', error);
      setError('Failed to load users: ' + error.message);
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  };

  const updateUserStatus = async (userId, isActive) => {
    try {
      await api.put(`/admin/users/${userId}/status`, { is_active: isActive });
      setUsers(prev => prev.map(user =>
        user.id === userId ? { ...user, is_active: isActive } : user
      ));
    } catch (error) {
      console.error('Error updating user status:', error);
    }
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setShowViewModal(true);
  };

  // The pencil now asks what the admin wants to do, rather than assuming edit
  const handleEditUser = (user) => {
    setSelectedUser(user);
    setResetResult(null);
    setShowActionModal(true);
  };

  const openEditProfile = () => {
    setEditForm({
      first_name: selectedUser.first_name,
      last_name: selectedUser.last_name,
      email: selectedUser.email,
      phone: selectedUser.phone
    });
    setShowActionModal(false);
    setShowEditModal(true);
  };

  const sendPasswordReset = async () => {
    setResetSending(true);
    setResetResult(null);
    try {
      const res = await api.post(`/admin/users/${selectedUser.id}/send-password-reset`);
      setResetResult({
        ok: true,
        message: res.data?.message || `Reset link sent to ${selectedUser.email}.`
      });
    } catch (error) {
      setResetResult({
        ok: false,
        message: error.response?.data?.detail || 'Could not send the reset email. Please try again.'
      });
    } finally {
      setResetSending(false);
    }
  };

  const openCreateCustomer = () => {
    setCreateForm(EMPTY_CUSTOMER);
    setCreateError(null);
    setCreateResult(null);
    setShowCreateModal(true);
  };

  const closeCreateCustomer = () => {
    if (createSaving) return;
    setShowCreateModal(false);
  };

  const setCreateField = (key) => (e) => {
    const value = e.target.value;
    setCreateForm(prev => ({ ...prev, [key]: value }));
    if (createError) setCreateError(null);
  };

  // The password is generated and emailed by the server and never comes back
  // here, so there is nothing sensitive for this screen to show or leak.
  const submitCreateCustomer = async (e) => {
    e.preventDefault();
    const f = createForm;
    if (!f.first_name.trim() || !f.last_name.trim() || !f.email.trim() || !f.phone.trim()) {
      setCreateError('First name, last name, email and phone are all required.');
      return;
    }
    if (f.user_type === 'business' && !f.company_name.trim()) {
      setCreateError('Company name is required for a business account.');
      return;
    }
    const payload = {
      first_name: f.first_name.trim(),
      last_name: f.last_name.trim(),
      email: f.email.trim(),
      phone: f.phone.trim(),
      user_type: f.user_type
    };
    if (f.user_type === 'business') {
      payload.company_name = f.company_name.trim();
      payload.business_type = f.business_type;
      if (f.gst_number.trim()) payload.gst_number = f.gst_number.trim();
    }
    setCreateSaving(true);
    setCreateError(null);
    try {
      const res = await api.post('/admin/customers', payload);
      setCreateResult(res.data?.message || `Login created for ${payload.email}.`);
      fetchUsers();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setCreateError(
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail) && detail[0]?.msg
            ? detail[0].msg
            : 'Could not create the login. Please try again.'
      );
    } finally {
      setCreateSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    try {
      await api.put(`/admin/users/${selectedUser.id}`, editForm);
      setUsers(prev => prev.map(user =>
        user.id === selectedUser.id ? { ...user, ...editForm } : user
      ));
      setShowEditModal(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Failed to update user: ' + error.message);
    }
  };

  const getUserTypeIcon = (userType) =>
    userType === 'business'
      ? <OfficeBuildingIcon className="w-3.5 h-3.5" />
      : <UserIcon className="w-3.5 h-3.5" />;

  const getVerificationIcon = (status) =>
    status === 'verified'
      ? <ShieldCheckIcon className="w-4 h-4 text-green-500" title="Verified" />
      : <ShieldExclamationIcon className="w-4 h-4 text-yellow-500" title="Pending verification" />;

  const initials = (u) => `${(u.first_name || '?')[0] || ''}${(u.last_name || '')[0] || ''}`.toUpperCase();
  const money = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;
  const fmtDate = formatDate;

  // Filtering (search + userType + verification) is done server-side; render as-is.
  const visibleUsers = users;

  const chipDefs = [
    { key: 'search', label: 'Search', value: filters.search },
    { key: 'userType', label: 'Type', value: filters.userType },
    { key: 'verificationStatus', label: 'Status', value: filters.verificationStatus }
  ].filter(c => c.value);
  const hasActiveFilters = chipDefs.length > 0;

  const inputCls =
    'border border-gray-300 rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-orange-500';

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">User Management</h2>
          <p className="text-xs text-gray-500">Manage customer accounts and user permissions</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={openCreateCustomer}
            className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium text-orange-700 bg-orange-50 border border-orange-200 hover:bg-orange-100"
          >
            <UserAddIcon className="w-4 h-4 mr-1.5" />
            Create customer login
          </button>
          <button
            onClick={fetchUsers}
            className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium text-white bg-orange-600 hover:bg-orange-700"
          >
            <RefreshIcon className="w-4 h-4 mr-1.5" />
            Refresh
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Compact filter toolbar */}
      <div className="bg-white shadow-sm rounded-lg p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <SearchIcon className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, email…"
            className={`${inputCls} pl-8 w-full`}
            value={filters.search}
            onChange={(e) => updateFilters({ search: e.target.value })}
          />
        </div>
        <select
          className={inputCls}
          value={filters.userType}
          onChange={(e) => updateFilters({ userType: e.target.value })}
        >
          <option value="">All types</option>
          <option value="individual">Individual</option>
          <option value="business">Business</option>
        </select>
        <select
          className={inputCls}
          value={filters.verificationStatus}
          onChange={(e) => updateFilters({ verificationStatus: e.target.value })}
        >
          <option value="">All statuses</option>
          <option value="verified">Verified</option>
          <option value="pending">Pending</option>
        </select>
      </div>

      {/* Active filter chips */}
      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-2">
          {chipDefs.map(chip => (
            <span
              key={chip.key}
              className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-full bg-orange-50 border border-orange-200 text-xs text-orange-800 capitalize"
            >
              <span className="font-medium">{chip.label}:</span> {chip.value}
              <button
                onClick={() => updateFilters({ [chip.key]: '' })}
                className="ml-0.5 rounded-full hover:bg-orange-200 p-0.5"
                title="Remove filter"
              >
                <XIcon className="h-3 w-3" />
              </button>
            </span>
          ))}
          <button
            onClick={() => resetFilters({ search: '', userType: '', verificationStatus: '' })}
            className="text-xs font-medium text-gray-500 hover:text-gray-700 underline"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700">
            Registered Users <span className="text-gray-400 font-normal">({pagination.total})</span>
          </h3>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr className="text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                  <th className="px-4 py-2">User</th>
                  <th className="px-4 py-2">Type</th>
                  <th className="px-4 py-2">Active</th>
                  <th className="px-4 py-2">Shipments</th>
                  <th className="px-4 py-2 text-right">Revenue</th>
                  <th className="px-4 py-2">Joined</th>
                  <th className="px-4 py-2">Last Login</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {visibleUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 whitespace-nowrap">
                      <div className="flex items-center gap-2.5">
                        <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-medium text-gray-600">{initials(user)}</span>
                        </div>
                        <div className="leading-tight">
                          <span className="text-sm font-medium text-gray-900">
                            {user.first_name} {user.last_name}
                          </span>
                          <span className="ml-2 text-xs text-gray-500">{user.email}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          user.user_type === 'business' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {getUserTypeIcon(user.user_type)}
                          {user.user_type}
                        </span>
                        {getVerificationIcon(user.verification_status)}
                        {user.must_change_password && (
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800"
                            title="Created by an admin. The customer has not replaced the temporary password yet."
                          >
                            Invite pending
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <label className="inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="form-checkbox h-4 w-4 text-orange-600 rounded"
                          checked={user.is_active}
                          onChange={(e) => updateUserStatus(user.id, e.target.checked)}
                        />
                        <span className={`ml-1.5 text-xs ${user.is_active ? 'text-green-700' : 'text-gray-400'}`}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </label>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">
                      {user.total_shipments}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                      {money(user.total_spent)}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                      {fmtDate(user.created_at)}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                      {user.last_login ? fmtDate(user.last_login) : 'Never'}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleViewUser(user)}
                          className="text-orange-600 hover:text-orange-900"
                          title="View Details"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEditUser(user)}
                          className="text-gray-500 hover:text-gray-900"
                          title="Edit User"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {visibleUsers.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-10 text-center text-sm text-gray-400">
                      No users found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <div className="bg-white px-4 py-2.5 flex items-center justify-between border-t border-gray-200">
          <p className="text-xs text-gray-600">
            {pagination.total > 0 ? (
              <>
                Showing <span className="font-medium">{((pagination.page - 1) * pagination.limit) + 1}</span>
                –<span className="font-medium">{Math.min(pagination.page * pagination.limit, pagination.total)}</span>
                {' '}of <span className="font-medium">{pagination.total}</span>
              </>
            ) : '—'}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
              disabled={pagination.page === 1}
              className="px-3 py-1 border border-gray-300 text-sm rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
              className="px-3 py-1 border border-gray-300 text-sm rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* View User Modal */}
      {showViewModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 w-full max-w-2xl shadow-lg rounded-lg bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-base font-semibold text-gray-900">User Details</h3>
              <button onClick={() => setShowViewModal(false)} className="text-gray-400 hover:text-gray-600">
                <XIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
              <div>
                <span className="text-xs text-gray-500">First Name</span>
                <p className="text-sm text-gray-900">{selectedUser.first_name}</p>
              </div>
              <div>
                <span className="text-xs text-gray-500">Last Name</span>
                <p className="text-sm text-gray-900">{selectedUser.last_name}</p>
              </div>
              <div>
                <span className="text-xs text-gray-500">Email</span>
                <p className="text-sm text-gray-900 break-words">{selectedUser.email}</p>
              </div>
              <div>
                <span className="text-xs text-gray-500">Phone</span>
                <p className="text-sm text-gray-900">{selectedUser.phone}</p>
              </div>
              <div>
                <span className="text-xs text-gray-500">User Type</span>
                <p className="text-sm text-gray-900 capitalize">{selectedUser.user_type}</p>
              </div>
              <div>
                <span className="text-xs text-gray-500">Status</span>
                <p className="text-sm text-gray-900">{selectedUser.is_active ? 'Active' : 'Inactive'}</p>
              </div>
              <div>
                <span className="text-xs text-gray-500">Total Shipments</span>
                <p className="text-sm text-gray-900">{selectedUser.total_shipments}</p>
              </div>
              <div>
                <span className="text-xs text-gray-500">Total Spent</span>
                <p className="text-sm text-gray-900">{money(selectedUser.total_spent)}</p>
              </div>
              <div>
                <span className="text-xs text-gray-500">Verification</span>
                <p className="text-sm text-gray-900 capitalize">{selectedUser.verification_status}</p>
              </div>
              <div>
                <span className="text-xs text-gray-500">Last Login</span>
                <p className="text-sm text-gray-900">
                  {selectedUser.last_login ? formatDateTime(selectedUser.last_login) : 'Never'}
                </p>
              </div>
              <div className="col-span-2">
                <span className="text-xs text-gray-500">Member Since</span>
                <p className="text-sm text-gray-900">{fmtDate(selectedUser.created_at)}</p>
              </div>
            </div>

            <div className="mt-5 flex justify-end">
              <button
                onClick={() => setShowViewModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-800 text-sm rounded-md hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Choose what to do with this user */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl bg-white p-6 shadow-xl">
            {createResult ? (
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Login created</h3>
                <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                  {createResult}
                </div>
                <p className="mt-3 text-xs text-gray-500">
                  If the customer cannot find the email, use "Send password reset link" on their
                  account. Completing that link also clears the forced password change.
                </p>
                <div className="mt-5 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={openCreateCustomer}
                    className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Create another
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="rounded-lg bg-orange-500 px-4 py-2 text-sm font-medium text-white hover:bg-orange-600"
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={submitCreateCustomer}>
                <h3 className="text-lg font-semibold text-gray-900">Create customer login</h3>
                <p className="mt-1 text-sm text-gray-500">
                  The customer is emailed a temporary password and must choose their own at first
                  sign-in. You will not see the password.
                </p>

                <div className="mt-4 grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">First name</label>
                    <input
                      type="text"
                      value={createForm.first_name}
                      onChange={setCreateField('first_name')}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-orange-400 focus:outline-none"
                      autoFocus
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Last name</label>
                    <input
                      type="text"
                      value={createForm.last_name}
                      onChange={setCreateField('last_name')}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-orange-400 focus:outline-none"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700">Email (their login ID)</label>
                    <input
                      type="email"
                      value={createForm.email}
                      onChange={setCreateField('email')}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-orange-400 focus:outline-none"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700">Phone</label>
                    <input
                      type="tel"
                      value={createForm.phone}
                      onChange={setCreateField('phone')}
                      placeholder="+91 98765 43210"
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-orange-400 focus:outline-none"
                    />
                  </div>
                  <div className="col-span-2">
                    <span className="block text-sm font-medium text-gray-700">Account type</span>
                    <div className="mt-1 grid grid-cols-2 gap-2">
                      {['individual', 'business'].map((type) => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => setCreateForm(prev => ({ ...prev, user_type: type }))}
                          className={`rounded-lg border px-3 py-2 text-sm font-medium capitalize ${
                            createForm.user_type === type
                              ? 'border-orange-400 bg-orange-50 text-orange-700'
                              : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {type}
                        </button>
                      ))}
                    </div>
                  </div>

                  {createForm.user_type === 'business' && (
                    <>
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700">Company name</label>
                        <input
                          type="text"
                          value={createForm.company_name}
                          onChange={setCreateField('company_name')}
                          className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-orange-400 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Business type</label>
                        <select
                          value={createForm.business_type}
                          onChange={setCreateField('business_type')}
                          className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-orange-400 focus:outline-none"
                        >
                          <option value="e-commerce">E-commerce</option>
                          <option value="manufacturing">Manufacturing</option>
                          <option value="retail">Retail</option>
                          <option value="services">Services</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">GST number (optional)</label>
                        <input
                          type="text"
                          value={createForm.gst_number}
                          onChange={setCreateField('gst_number')}
                          className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-orange-400 focus:outline-none"
                        />
                      </div>
                    </>
                  )}
                </div>

                {createError && (
                  <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700" role="alert">
                    {createError}
                  </div>
                )}

                <div className="mt-5 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={closeCreateCustomer}
                    className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createSaving}
                    className="rounded-lg bg-orange-500 px-4 py-2 text-sm font-medium text-white hover:bg-orange-600 disabled:opacity-60"
                  >
                    {createSaving ? 'Creating...' : 'Create login & send invite'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {showActionModal && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">Manage user</h3>
            <p className="mt-1 text-sm text-gray-500">
              {selectedUser.first_name} {selectedUser.last_name} &middot; {selectedUser.email}
            </p>

            {resetResult ? (
              <div
                className={`mt-4 rounded-lg border p-3 text-sm ${
                  resetResult.ok
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                    : 'border-red-200 bg-red-50 text-red-700'
                }`}
              >
                {resetResult.message}
              </div>
            ) : null}

            <div className="mt-5 space-y-3">
              <button
                type="button"
                onClick={openEditProfile}
                className="flex w-full items-center gap-3 rounded-lg border border-gray-200 p-3 text-left hover:border-orange-300 hover:bg-orange-50"
              >
                <PencilIcon className="h-5 w-5 shrink-0 text-orange-500" />
                <span>
                  <span className="block font-medium text-gray-900">Edit profile</span>
                  <span className="block text-xs text-gray-500">
                    Change name, email or phone
                  </span>
                </span>
              </button>

              <button
                type="button"
                onClick={sendPasswordReset}
                disabled={resetSending}
                className="flex w-full items-center gap-3 rounded-lg border border-gray-200 p-3 text-left hover:border-orange-300 hover:bg-orange-50 disabled:opacity-60"
              >
                <ShieldCheckIcon className="h-5 w-5 shrink-0 text-orange-500" />
                <span>
                  <span className="block font-medium text-gray-900">
                    {resetSending ? 'Sending reset link...' : 'Send password reset link'}
                  </span>
                  <span className="block text-xs text-gray-500">
                    Emails the customer a link, valid 2 hours. You never see their password.
                  </span>
                </span>
              </button>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowActionModal(false);
                  setSelectedUser(null);
                  setResetResult(null);
                }}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 w-full max-w-2xl shadow-lg rounded-lg bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-base font-semibold text-gray-900">Edit User</h3>
              <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-gray-600">
                <XIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">First Name</label>
                <input
                  type="text"
                  value={editForm.first_name}
                  onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                  className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Last Name</label>
                <input
                  type="text"
                  value={editForm.last_name}
                  onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                  className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Email</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Phone</label>
                <input
                  type="tel"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                />
              </div>
            </div>

            <div className="mt-5 flex justify-end space-x-2">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-800 text-sm rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-4 py-2 bg-orange-600 text-white text-sm rounded-md hover:bg-orange-700"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
